import { EmissionSourceType } from "@service/shared/enums";
import { ReactElement } from "react";
import { Route, Routes } from "react-router-dom";

import { CCF_DATA_ENTRY_ROUTE_SEGMENTS, CCF_ROUTE_SEGMENTS } from "../../shared/routing/paths/ccf";
import { DataEntryPageEmissionSourceContext } from "./data-entry-pages/shared/data-entry-page-emission-source";
import {
  DataEntryPage,
  DirectCombustion,
  DirectCooling,
  DirectElectricity,
  DirectFugitive,
  Disposal,
  EmployeeCommutingMain,
  ExternalDataCenter,
  ExternallyCalculated,
  Flight,
  FoodAndBeverages,
  Heating,
  HotelStays,
  InboundLogisticsContainer,
  IntralogisticsContainer,
  MaterialDataEntryPage,
  OutboundLogisticsContainer,
  PrivateAndRentalVehicle,
  ProcessingOfSoldProductsWaste,
  ProductDisposal,
  RailTravel,
  Scope3Combustion,
  Scope3Cooling,
  Scope3Electricity,
  Scope3Fugitive,
  VehicleFleet,
  Water,
} from "./lazy-pages";

/**
 * The page behind each emission source, in the order of `CCF_DATA_ENTRY_ROUTE_SEGMENTS`, which
 * supplies the matching URL segments. `satisfies` keeps the two records in step: adding an emission
 * source to the enum without a page here is a compile error, not a route that silently goes missing.
 */
const DATA_ENTRY_PAGES = {
  [EmissionSourceType.ELECTRICITY]: <DirectElectricity />,
  [EmissionSourceType.DIRECT_COMBUSTION]: <DirectCombustion />,
  [EmissionSourceType.COOLING]: <DirectCooling />,
  [EmissionSourceType.HEATING_AND_FUEL]: <Heating />,
  [EmissionSourceType.VEHICLE_FLEET]: <VehicleFleet />,
  [EmissionSourceType.DIRECT_FUGITIVE]: <DirectFugitive />,
  [EmissionSourceType.PRODUCTION_MATERIALS]: <MaterialDataEntryPage emissionSourceKey="productionMaterials" />,
  [EmissionSourceType.PACKAGING]: <MaterialDataEntryPage emissionSourceKey="packaging" />,
  [EmissionSourceType.PURCHASED_SERVICE]: <ExternallyCalculated emissionSourceKey="purchasedService" />,
  [EmissionSourceType.OPERATING_SUPPLIES]: <MaterialDataEntryPage emissionSourceKey="operatingSupplies" />,
  [EmissionSourceType.FOOD_AND_BEVERAGES]: <FoodAndBeverages />,
  [EmissionSourceType.EXTERNAL_DATA_CENTER]: <ExternalDataCenter />,
  [EmissionSourceType.WATER]: <Water />,
  [EmissionSourceType.CAPITAL_GOODS]: <ExternallyCalculated emissionSourceKey="capitalGoods" />,
  [EmissionSourceType.DISPOSAL]: <Disposal />,
  [EmissionSourceType.PRIVATE_AND_RENTAL_VEHICLE]: <PrivateAndRentalVehicle />,
  [EmissionSourceType.RAIL_TRAVEL]: <RailTravel />,
  [EmissionSourceType.FLIGHT]: <Flight />,
  [EmissionSourceType.HOTEL_STAYS]: <HotelStays />,
  [EmissionSourceType.EMPLOYEE_COMMUTING]: <EmployeeCommutingMain />,
  [EmissionSourceType.UPSTREAM_LEASED_ASSETS_ELECTRICITY]: (
    <Scope3Electricity emissionSourceKey="upstreamLeasedAssetsElectricity" />
  ),
  [EmissionSourceType.UPSTREAM_LEASED_ASSETS_COMBUSTION]: (
    <Scope3Combustion emissionSourceKey="upstreamLeasedAssetsCombustion" />
  ),
  [EmissionSourceType.UPSTREAM_LEASED_ASSETS_COOLING]: (
    <Scope3Cooling emissionSourceKey="upstreamLeasedAssetsCooling" />
  ),
  [EmissionSourceType.UPSTREAM_LEASED_ASSETS_FUGITIVE]: (
    <Scope3Fugitive emissionSourceKey="upstreamLeasedAssetsFugitive" />
  ),
  [EmissionSourceType.INBOUND_LOGISTICS]: <InboundLogisticsContainer />,
  [EmissionSourceType.INTRALOGISTICS]: <IntralogisticsContainer />,
  [EmissionSourceType.OUTBOUND_LOGISTICS]: <OutboundLogisticsContainer />,
  [EmissionSourceType.STORAGE_UPSTREAM]: <ExternallyCalculated emissionSourceKey="storageUpstream" />,
  [EmissionSourceType.STORAGE_DOWNSTREAM]: <ExternallyCalculated emissionSourceKey="storageDownstream" />,
  [EmissionSourceType.PROCESSING_OF_SOLD_PRODUCTS_ELECTRICITY]: (
    <Scope3Electricity emissionSourceKey="processingOfSoldProductsElectricity" />
  ),
  [EmissionSourceType.PROCESSING_OF_SOLD_PRODUCTS_COMBUSTION]: (
    <Scope3Combustion emissionSourceKey="processingOfSoldProductsCombustion" />
  ),
  [EmissionSourceType.PROCESSING_OF_SOLD_PRODUCTS_COOLING]: (
    <Scope3Cooling emissionSourceKey="processingOfSoldProductsCooling" />
  ),
  [EmissionSourceType.PROCESSING_OF_SOLD_PRODUCTS_FUGITIVE]: (
    <Scope3Fugitive emissionSourceKey="processingOfSoldProductsFugitive" />
  ),
  [EmissionSourceType.PROCESSING_OF_SOLD_PRODUCTS_WASTE]: <ProcessingOfSoldProductsWaste />,
  [EmissionSourceType.USE_OF_SOLD_PRODUCTS_ELECTRICITY]: (
    <Scope3Electricity emissionSourceKey="useOfSoldProductsElectricity" />
  ),
  [EmissionSourceType.USE_OF_SOLD_PRODUCTS_COMBUSTION]: (
    <Scope3Combustion emissionSourceKey="useOfSoldProductsCombustion" />
  ),
  [EmissionSourceType.USE_OF_SOLD_PRODUCTS_COOLING]: <Scope3Cooling emissionSourceKey="useOfSoldProductsCooling" />,
  [EmissionSourceType.USE_OF_SOLD_PRODUCTS_FUGITIVE]: <Scope3Fugitive emissionSourceKey="useOfSoldProductsFugitive" />,
  [EmissionSourceType.USE_OF_SOLD_PRODUCTS_OTHER]: <ExternallyCalculated emissionSourceKey="useOfSoldProductsOther" />,
  [EmissionSourceType.PRODUCT_DISPOSAL]: <ProductDisposal />,
  [EmissionSourceType.DOWNSTREAM_LEASED_ASSETS_ELECTRICITY]: (
    <Scope3Electricity emissionSourceKey="downstreamLeasedAssetsElectricity" />
  ),
  [EmissionSourceType.DOWNSTREAM_LEASED_ASSETS_COMBUSTION]: (
    <Scope3Combustion emissionSourceKey="downstreamLeasedAssetsCombustion" />
  ),
  [EmissionSourceType.DOWNSTREAM_LEASED_ASSETS_COOLING]: (
    <Scope3Cooling emissionSourceKey="downstreamLeasedAssetsCooling" />
  ),
  [EmissionSourceType.DOWNSTREAM_LEASED_ASSETS_FUGITIVE]: (
    <Scope3Fugitive emissionSourceKey="downstreamLeasedAssetsFugitive" />
  ),
  [EmissionSourceType.FRANCHISE]: <ExternallyCalculated emissionSourceKey="franchise" />,
  [EmissionSourceType.INVESTMENT]: <ExternallyCalculated emissionSourceKey="investment" />,
} as const satisfies Record<EmissionSourceType, ReactElement>;

export const DataCollection = {
  path: CCF_ROUTE_SEGMENTS.calculationId,
  children: [
    {
      element: <DataEntryPage />,
      children: [
        ...Object.entries(DATA_ENTRY_PAGES).map(([emissionSourceType, page]) => ({
          path: `${CCF_DATA_ENTRY_ROUTE_SEGMENTS[emissionSourceType as EmissionSourceType]}/*`,
          element: preserveRoutingWithMassUpload(
            <DataEntryPageEmissionSourceContext.Provider value={emissionSourceType as EmissionSourceType}>
              {page}
            </DataEntryPageEmissionSourceContext.Provider>,
            emissionSourceType
          ),
        })),
      ],
    },
  ],
};

/**
 * A descendant `<Routes>` left over from the `createBrowserRouter` migration (CFC-828). Together
 * with the `/*` on every path above it renders the page at the exact segment and nothing below it.
 * Nothing on these pages has been found to need it, but dropping it changes what deep URLs render,
 * so it wants a browser check rather than a type check.
 *
 * `routeKey` is not decoration. These sibling routes render at the same position in the tree, and
 * six emission sources share `ExternallyCalculated` while four each share the `Scope3*` pages.
 * Without a key React reconciles one into the next instead of remounting, and the previous source's
 * state stays on screen. `data-collection.unit.ts` asserts the keys stay distinct.
 */
function preserveRoutingWithMassUpload(element: ReactElement, routeKey: string): ReactElement {
  return (
    <Routes key={routeKey}>
      <Route path="/" element={element} />
    </Routes>
  );
}
